import React, { useState, useEffect } from 'react';
import { getFirestore, doc, setDoc, updateDoc, serverTimestamp, collection, getDocs, writeBatch, deleteDoc } from 'firebase/firestore';
import { geohashForLocation } from 'geofire-common';
import Swal from 'sweetalert2';
import { inferOrderType, enabledOrderTypeKeys } from './orderTypeUtils';

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

const normalizeCountryCode = (country) => {
  if (!country) return 'US';
  const c = country.trim();
  if (c.length === 2 && /^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
  const map = {
    'united states': 'US',
    'united states of america': 'US',
    'usa': 'US',
    'canada': 'CA',
    'mexico': 'MX',
    'united kingdom': 'GB',
    'great britain': 'GB',
    'france': 'FR',
    'germany': 'DE',
    'spain': 'ES',
    'italy': 'IT',
    'australia': 'AU',
    'brazil': 'BR',
    'india': 'IN',
    'japan': 'JP',
    'china': 'CN',
    'south korea': 'KR',
    'netherlands': 'NL',
    'belgium': 'BE',
    'switzerland': 'CH',
    'austria': 'AT',
    'sweden': 'SE',
    'norway': 'NO',
    'denmark': 'DK',
    'finland': 'FI',
    'ireland': 'IE',
    'portugal': 'PT',
    'greece': 'GR',
    'poland': 'PL',
    'czech republic': 'CZ',
    'hungary': 'HU',
    'romania': 'RO',
    'bulgaria': 'BG',
    'croatia': 'HR',
    'slovenia': 'SI',
    'slovakia': 'SK',
    'lithuania': 'LT',
    'latvia': 'LV',
    'estonia': 'EE',
    'luxembourg': 'LU',
    'malta': 'MT',
    'cyprus': 'CY',
  };
  const lower = c.toLowerCase();
  if (map[lower]) return map[lower];
  return c;
};

// Reads the location's orderTypes subcollection and returns the keys of the
// enabled ones (e.g. ['delivery', 'pickup']) — exactly what gets published
// to publicStores/{storeId}.orderTypes and what the consumer apps filter on.
// Defensive: legacy docs with only `{name}` are normalized on read.
const loadEnabledOrderTypeKeys = async (db, uid, locationId) => {
  try {
    const snap = await getDocs(
      collection(db, 'users', uid, 'locations', locationId, 'orderTypes')
    );
    const types = snap.docs.map((d) => inferOrderType(d.id, d.data()));
    return enabledOrderTypeKeys(types);
  } catch (e) {
    console.warn('Error loading orderTypes for public store sync:', e);
    return [];
  }
};

const SiteInformationTab = ({ user, location, publicStore, setPublicStore }) => {
  const [deploying, setDeploying] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [formData, setFormData] = useState({
    locationName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    websiteUrl: '',
    contactEmail: '',
    logoUrl: '',
    latitude: null,
    longitude: null,
  });
  const [publishNow, setPublishNow] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [suggestedUrl, setSuggestedUrl] = useState('');

  useEffect(() => {
    if (location) {
      setFormData({
        locationName: location.companyName || location.locationName || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        zip: location.zip || '',
        country: location.country || '',
        phone: location.phone || '',
        websiteUrl: location.websiteUrl || '',
        contactEmail: location.contactEmail || user?.email || '',
        logoUrl: location.logoUrl || '',
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
      });
      // Generate suggested URL slug
      const slug = (location.companyName || location.locationName || 'store')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      // The REAL public URL pattern (public-web): /store/{slug}-{locationId}
      // — the old hint ({slug}.fodyfee.com) pointed at subdomains that don't
      // exist (no wildcard DNS) and opened nothing.
      const publicBase = process.env.REACT_APP_PUBLIC_WEB_URL || 'https://go.fodyfee.com';
      setSuggestedUrl(`${publicBase}/store/${slug}-${location.id || location.locationId || ''}`);
    }
  }, [location, user]);

  const searchAddress = async (query) => {
    if (!query || query.length < 3 || !MAPBOX_ACCESS_TOKEN) return;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&limit=5`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Mapbox search error:', error);
    }
  };

  const selectAddress = (place) => {
    const [longitude, latitude] = place.center || [null, null];
    const context = place.context || [];
    let city = '';
    let state = '';
    let country = '';
    let zip = '';
    context.forEach((item) => {
      if (item.id.startsWith('place.')) city = item.text;
      if (item.id.startsWith('region.')) state = item.text;
      if (item.id.startsWith('country.')) country = item.short_code || item.text;
      if (item.id.startsWith('postcode.')) zip = item.text;
    });
    setFormData({
      ...formData,
      address: place.place_name,
      city,
      state,
      zip,
      country: normalizeCountryCode(country),
      latitude,
      longitude,
    });
    setShowAddressSuggestions(false);
  };

  // Load admin menu data filtered by availableLocations for the target location
  const loadAdminMenuData = async (uid, targetLocationId) => {
    const db = getFirestore();
    console.log('[OnlineOrdering] Loading admin menu data for uid:', uid, 'location:', targetLocationId);
    const catsSnap = await getDocs(collection(db, 'users', uid, 'categories'));
    const categories = [];
    const products = [];
    console.log('[OnlineOrdering] Admin categories found:', catsSnap.docs.length);
    for (const catDoc of catsSnap.docs) {
      const cat = { id: catDoc.id, ...catDoc.data() };
      // Only include categories available for this location
      const availableLocs = cat.availableLocations;
      const isAvailable = Array.isArray(availableLocs) && availableLocs.includes(targetLocationId);
      if (!isAvailable) {
        console.log(`[OnlineOrdering] Category "${cat.name}" (${cat.id}) skipped — not available for location ${targetLocationId}`);
        continue;
      }
      categories.push(cat);
      const prodSnap = await getDocs(collection(db, 'users', uid, 'categories', cat.id, 'products'));
      console.log(`[OnlineOrdering] Category "${cat.name}" (${cat.id}) has ${prodSnap.docs.length} products`);
      prodSnap.docs.forEach((p) => {
        const pdata = p.data();
        // Product-level location scoping: empty/missing availableLocations =
        // all locations; otherwise publish only to the listed locations.
        const pLocs = pdata.availableLocations;
        if (Array.isArray(pLocs) && pLocs.length > 0 && !pLocs.includes(targetLocationId)) return;
        products.push({ id: p.id, categoryId: cat.id, ...pdata });
      });
    }
    const optsSnap = await getDocs(collection(db, 'users', uid, 'productOptions'));
    const options = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    console.log('[OnlineOrdering] Admin options found:', options.length);
    console.log('[OnlineOrdering] Total admin items for location', targetLocationId, ':', { categories: categories.length, products: products.length, options: options.length });
    return { categories, products, options };
  };

  // Load public store menu data
  const loadPublicMenuData = async (locationId) => {
    const db = getFirestore();
    const catsSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories'));
    const categories = [];
    const products = [];
    for (const catDoc of catsSnap.docs) {
      const cat = { id: catDoc.id, ...catDoc.data() };
      categories.push(cat);
      const prodSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories', cat.id, 'products'));
      prodSnap.docs.forEach((p) => products.push({ id: p.id, categoryId: cat.id, ...p.data() }));
    }
    const optsSnap = await getDocs(collection(db, 'publicStores', locationId, 'productOptions'));
    const options = optsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { categories, products, options };
  };

  const calculateDiff = (admin, pub) => {
    const diff = { categories: { added: [], updated: [], removed: [] }, products: { added: [], updated: [], removed: [] }, options: { added: [], updated: [], removed: [] } };

    const compareLists = (aList, pList, type) => {
      const aMap = new Map(aList.map((i) => [i.id, i]));
      const pMap = new Map(pList.map((i) => [i.id, i]));
      aList.forEach((i) => {
        if (!pMap.has(i.id)) diff[type].added.push(i.name || i.locationName || 'Untitled');
        else {
          const pubItem = pMap.get(i.id);
          const aUpdated = i.updatedAt?.toMillis?.() || i.updatedAt?.seconds * 1000 || 0;
          const pUpdated = pubItem.updatedAt?.toMillis?.() || pubItem.updatedAt?.seconds * 1000 || 0;
          if (aUpdated > pUpdated) diff[type].updated.push(i.name || i.locationName || 'Untitled');
        }
      });
      pList.forEach((i) => {
        if (!aMap.has(i.id)) diff[type].removed.push(i.name || i.locationName || 'Untitled');
      });
    };

    compareLists(admin.categories, pub.categories, 'categories');
    compareLists(admin.products, pub.products, 'products');
    compareLists(admin.options, pub.options, 'options');
    return diff;
  };

  const buildDiffHtml = (diff) => {
    let html = '<div style="text-align:left;max-height:300px;overflow-y:auto;">';
    const section = (title, items) => {
      if (!items.added.length && !items.updated.length && !items.removed.length) return '';
      let s = `<h4 style="margin:10px 0 5px;font-size:14px;color:#333;">${title}</h4><ul style="margin:0 0 10px 20px;padding:0;">`;
      items.added.forEach((n) => (s += `<li style="color:#28a745;"><strong>+</strong> ${n} <span style="font-size:11px;color:#666;">(new)</span></li>`));
      items.updated.forEach((n) => (s += `<li style="color:#ffc107;"><strong>~</strong> ${n} <span style="font-size:11px;color:#666;">(modified)</span></li>`));
      items.removed.forEach((n) => (s += `<li style="color:#dc3545;"><strong>-</strong> ${n} <span style="font-size:11px;color:#666;">(removed)</span></li>`));
      s += '</ul>';
      return s;
    };
    html += section('Categories', diff.categories);
    html += section('Products', diff.products);
    html += section('Product Options', diff.options);
    if (html === '<div style="text-align:left;max-height:300px;overflow-y:auto;">') {
      html += '<p style="color:#666;">No changes detected. Data is up to date.</p>';
    }
    html += '</div>';
    return html;
  };

  const syncMenuDataToPublicStore = async (uid, locationId) => {
    const db = getFirestore();
    const { categories, products, options } = await loadAdminMenuData(uid, locationId);
    console.log('[OnlineOrdering] Starting sync to publicStores/', locationId);

    // Delete existing public store menu data
    let deletedCats = 0;
    let deletedProds = 0;
    let deletedOpts = 0;
    const pubCatsSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories'));
    for (const catDoc of pubCatsSnap.docs) {
      const prodSnap = await getDocs(collection(db, 'publicStores', locationId, 'categories', catDoc.id, 'products'));
      for (const p of prodSnap.docs) {
        await deleteDoc(doc(db, 'publicStores', locationId, 'categories', catDoc.id, 'products', p.id));
        deletedProds++;
      }
      await deleteDoc(doc(db, 'publicStores', locationId, 'categories', catDoc.id));
      deletedCats++;
    }
    const pubOptsSnap = await getDocs(collection(db, 'publicStores', locationId, 'productOptions'));
    for (const o of pubOptsSnap.docs) {
      await deleteDoc(doc(db, 'publicStores', locationId, 'productOptions', o.id));
      deletedOpts++;
    }
    console.log('[OnlineOrdering] Deleted existing:', { categories: deletedCats, products: deletedProds, options: deletedOpts });

    // Write new data
    let currentBatch = writeBatch(db);
    let opCount = 0;

    const commitIfNeeded = async () => {
      if (opCount >= 450) {
        console.log('[OnlineOrdering] Committing batch at', opCount, 'operations');
        await currentBatch.commit();
        opCount = 0;
        currentBatch = writeBatch(db);
      }
    };

    for (const cat of categories) {
      const { id, ...data } = cat;
      currentBatch.set(doc(db, 'publicStores', locationId, 'categories', id), data);
      opCount++;
      await commitIfNeeded();
    }
    for (const prod of products) {
      const { id, categoryId, ...data } = prod;
      currentBatch.set(doc(db, 'publicStores', locationId, 'categories', categoryId, 'products', id), data);
      opCount++;
      await commitIfNeeded();
    }
    for (const opt of options) {
      const { id, ...data } = opt;
      currentBatch.set(doc(db, 'publicStores', locationId, 'productOptions', id), data);
      opCount++;
      await commitIfNeeded();
    }
    if (opCount > 0) {
      console.log('[OnlineOrdering] Committing final batch with', opCount, 'operations');
      await currentBatch.commit();
    }

    console.log('[OnlineOrdering] Sync complete. Written:', { categories: categories.length, products: products.length, options: options.length });
    return { categories, products, options };
  };

  const handleDeploy = async () => {
    if (!user || !location) return;
    if (!formData.locationName.trim()) {
      Swal.fire('Error', 'Location name is required.', 'error');
      return;
    }
    if (!termsAccepted) {
      Swal.fire('Error', 'You must accept the Terms and Conditions to deploy.', 'error');
      return;
    }

    setDeploying(true);
    try {
      const db = getFirestore();
      const locationId = location.id;
      const uid = user.uid;

      // Sync menu data
      setDeploying(true); // keep loader
      await syncMenuDataToPublicStore(uid, locationId);

      const lat = formData.latitude ?? null;
      const lng = formData.longitude ?? null;
      const geohash = (lat != null && lng != null) ? geohashForLocation([lat, lng]) : null;

      // Publish the enabled order types (delivery/pickup/dine_in/custom keys)
      // so the consumer apps can filter which stores to show per mode.
      const orderTypeKeys = await loadEnabledOrderTypeKeys(db, uid, locationId);

      const publicStoreData = {
        businessId: uid,
        locationId: locationId,
        locationName: formData.locationName.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        websiteUrl: formData.websiteUrl.trim(),
        contactEmail: formData.contactEmail.trim(),
        logoUrl: formData.logoUrl.trim(),
        coverImages: location?.coverImages || [],
        latitude: lat,
        longitude: lng,
        geohash,
        taxRate: location?.taxRate || 0,
        defaultFulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        // NOTE: this was missing from publicStoreData entirely — the
        // public checkout (public-web/CheckoutPage.js) always showed and
        // allowed "Cash" regardless of what the merchant configured on
        // this same "Accept Cash Payments" toggle, because the field never
        // made it into the document the public checkout actually reads.
        acceptCash: location?.acceptCash !== false,
        acceptCashByChannel: location?.acceptCashByChannel || null,
        confirmAllOrders: location?.confirmAllOrders === true,
        enableFodyesRewards: location?.enableFodyesRewards === true,
        fodyesCoinValue: location?.fodyesCoinValue || 0,
        fodyesReturnPercentage: location?.fodyesReturnPercentage || 0,
        maxFodyesDiscount: location?.maxFodyesDiscount || 0,
        minOrderForFodyes: location?.minOrderForFodyes || 0,
        operationHours: location?.operationHours || [],
        tags: location?.tags || [],
        orderTypes: orderTypeKeys,
        reservationSettings: location?.reservationSettings || null,
        queueSettings: location?.queueSettings || null,
        // Delivery fulfillment provider. Default-ON while Uber Direct is the
        // only provider (transparent for merchant + consumer); merchants can
        // opt out per location (deliveryProvider: 'none' → manual delivery).
        deliveryProvider: location?.deliveryProvider ?? 'uber_direct',
        // Delivery zone (radius miles / zip codes) — the public checkouts use
        // it to hide or block delivery outside the coverage area.
        deliverySettings: location?.deliverySettings || null,
        published: publishNow,
        status: publishNow ? 'published' : 'preparing',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        menuSyncedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'publicStores', locationId), publicStoreData);

      await updateDoc(doc(db, 'users', uid, 'locations', locationId), {
        publicStoreId: locationId,
        updatedAt: serverTimestamp(),
      });

      // Update user-level storePublished flag for setup guide tracking
      await updateDoc(doc(db, 'users', uid), {
        storePublished: publishNow,
      }, { merge: true });

      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));

      Swal.fire({
        title: publishNow ? 'Published!' : 'Profile Created!',
        text: publishNow
          ? 'Your online ordering profile is now live with menu data synced.'
          : 'Your online ordering profile has been created and is in preparing mode. Menu data has been synced.',
        icon: 'success',
      });

      setPublicStore({ id: locationId, ...publicStoreData });
    } catch (error) {
      console.error('Error deploying public store:', error);
      Swal.fire('Error', 'Failed to deploy public store. ' + error.message, 'error');
    } finally {
      setDeploying(false);
    }
  };

  const handleReDeploy = async () => {
    if (!user || !location || !publicStore) return;
    const locationId = location.id;
    const uid = user.uid;

    setSyncing(true);
    try {
      const adminData = await loadAdminMenuData(uid, locationId);
      const pubData = await loadPublicMenuData(locationId);
      const diff = calculateDiff(adminData, pubData);

      const diffHtml = buildDiffHtml(diff);
      const result = await Swal.fire({
        title: 'Review Changes',
        html: diffHtml,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Sync & Save Changes',
        cancelButtonText: 'Cancel',
        width: '600px',
      });

      if (!result.isConfirmed) {
        setSyncing(false);
        return;
      }

      await syncMenuDataToPublicStore(uid, locationId);
      const reLat = formData.latitude ?? null;
      const reLng = formData.longitude ?? null;
      const reGeohash = (reLat != null && reLng != null) ? geohashForLocation([reLat, reLng]) : null;
      const reOrderTypeKeys = await loadEnabledOrderTypeKeys(getFirestore(), uid, locationId);
      await updateDoc(doc(getFirestore(), 'publicStores', locationId), {
        updatedAt: serverTimestamp(),
        menuSyncedAt: serverTimestamp(),
        taxRate: location?.taxRate || 0,
        defaultFulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        acceptCash: location?.acceptCash !== false,
        acceptCashByChannel: location?.acceptCashByChannel || null,
        confirmAllOrders: location?.confirmAllOrders === true,
        enableFodyesRewards: location?.enableFodyesRewards === true,
        fodyesCoinValue: location?.fodyesCoinValue || 0,
        fodyesReturnPercentage: location?.fodyesReturnPercentage || 0,
        maxFodyesDiscount: location?.maxFodyesDiscount || 0,
        minOrderForFodyes: location?.minOrderForFodyes || 0,
        operationHours: location?.operationHours || [],
        tags: location?.tags || [],
        coverImages: location?.coverImages || [],
        orderTypes: reOrderTypeKeys,
        reservationSettings: location?.reservationSettings || null,
        queueSettings: location?.queueSettings || null,
        // Delivery fulfillment provider. Default-ON while Uber Direct is the
        // only provider (transparent for merchant + consumer); merchants can
        // opt out per location (deliveryProvider: 'none' → manual delivery).
        deliveryProvider: location?.deliveryProvider ?? 'uber_direct',
        // Delivery zone (radius miles / zip codes) — the public checkouts use
        // it to hide or block delivery outside the coverage area.
        deliverySettings: location?.deliverySettings || null,
        latitude: reLat,
        longitude: reLng,
        geohash: reGeohash,
      });

      // Ask to re-publish
      const pubResult = await Swal.fire({
        title: 'Re-publish Store?',
        text: 'Your store data has been refreshed. Do you want to publish these changes live now?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Publish Now',
        cancelButtonText: 'Keep as Preparing',
      });

      const userDb = getFirestore();
      const userRef = doc(userDb, 'users', user.uid);

      if (pubResult.isConfirmed) {
        await updateDoc(doc(userDb, 'publicStores', locationId), {
          published: true,
          status: 'published',
          updatedAt: serverTimestamp(),
        });
        await updateDoc(userRef, { storePublished: true }, { merge: true });
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        setPublicStore({ ...publicStore, published: true, status: 'published' });
        Swal.fire('Published!', 'Your updated store is now live.', 'success');
      } else {
        // If it was published before, maybe unpublish it until reviewed
        if (publicStore.published) {
          await updateDoc(doc(userDb, 'publicStores', locationId), {
            published: false,
            status: 'preparing',
            updatedAt: serverTimestamp(),
          });
          await updateDoc(userRef, { storePublished: false }, { merge: true });
          setPublicStore({ ...publicStore, published: false, status: 'preparing' });
          window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        }
        Swal.fire('Saved!', 'Changes saved. Your store remains in preparing mode.', 'success');
      }
    } catch (error) {
      console.error('Error re-deploying:', error);
      Swal.fire('Error', 'Failed to re-deploy. ' + error.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateInfo = async () => {
    if (!user || !location || !publicStore) return;
    setDeploying(true);
    try {
      const db = getFirestore();
      const locationId = location.id;

      const upLat = formData.latitude ?? null;
      const upLng = formData.longitude ?? null;
      const upGeohash = (upLat != null && upLng != null) ? geohashForLocation([upLat, upLng]) : null;
      const upOrderTypeKeys = await loadEnabledOrderTypeKeys(db, user.uid, locationId);
      await updateDoc(doc(db, 'publicStores', locationId), {
        locationName: formData.locationName.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        websiteUrl: formData.websiteUrl.trim(),
        contactEmail: formData.contactEmail.trim(),
        logoUrl: formData.logoUrl.trim(),
        latitude: upLat,
        longitude: upLng,
        geohash: upGeohash,
        taxRate: location?.taxRate || 0,
        defaultFulfillmentStatus: location?.defaultFulfillmentStatus || 'pending',
        acceptCash: location?.acceptCash !== false,
        acceptCashByChannel: location?.acceptCashByChannel || null,
        confirmAllOrders: location?.confirmAllOrders === true,
        enableFodyesRewards: location?.enableFodyesRewards === true,
        fodyesCoinValue: location?.fodyesCoinValue || 0,
        fodyesReturnPercentage: location?.fodyesReturnPercentage || 0,
        maxFodyesDiscount: location?.maxFodyesDiscount || 0,
        minOrderForFodyes: location?.minOrderForFodyes || 0,
        operationHours: location?.operationHours || [],
        tags: location?.tags || [],
        orderTypes: upOrderTypeKeys,
        reservationSettings: location?.reservationSettings || null,
        queueSettings: location?.queueSettings || null,
        // Delivery fulfillment provider. Default-ON while Uber Direct is the
        // only provider (transparent for merchant + consumer); merchants can
        // opt out per location (deliveryProvider: 'none' → manual delivery).
        deliveryProvider: location?.deliveryProvider ?? 'uber_direct',
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, 'users', user.uid, 'locations', locationId), {
        updatedAt: serverTimestamp(),
      });

      setPublicStore({
        ...publicStore,
        locationName: formData.locationName.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        websiteUrl: formData.websiteUrl.trim(),
        contactEmail: formData.contactEmail.trim(),
        logoUrl: formData.logoUrl.trim(),
        latitude: formData.latitude ?? null,
        longitude: formData.longitude ?? null,
      });

      Swal.fire('Updated!', 'Business information has been updated.', 'success');
    } catch (error) {
      console.error('Error updating public store:', error);
      Swal.fire('Error', 'Failed to update business information.', 'error');
    } finally {
      setDeploying(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!user || !location || !publicStore) return;
    const newStatus = !publicStore.published;
    try {
      const db = getFirestore();
      const locationId = location.id;
      await updateDoc(doc(db, 'publicStores', locationId), {
        published: newStatus,
        status: newStatus ? 'published' : 'preparing',
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'users', user.uid), { storePublished: newStatus }, { merge: true });
      setPublicStore({ ...publicStore, published: newStatus, status: newStatus ? 'published' : 'preparing' });
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire({
        title: newStatus ? 'Published!' : 'Unpublished',
        text: newStatus ? 'Your online ordering profile is now live.' : 'Your online ordering profile is now in preparing mode.',
        icon: 'success',
      });
    } catch (error) {
      console.error('Error toggling publish status:', error);
      Swal.fire('Error', 'Failed to update publish status.', 'error');
    }
  };

  const renderDeployForm = () => (
    <div className="row">
      <div className="col-lg-8 mx-auto">
        <div className="card card-outline card-primary">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-rocket mr-2"></i>
              Deploy Online Ordering Profile
            </h3>
          </div>
          <div className="card-body">
            <p className="text-muted">
              Create a public profile for <strong>{location.companyName || location.locationName}</strong>.
              This will allow customers to view your store and place orders online.
            </p>

            {suggestedUrl && (
              <div className="alert alert-info">
                <i className="fas fa-lightbulb mr-2"></i>
                <strong>Suggested Store URL:</strong>{' '}
                <a href={suggestedUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                  {suggestedUrl}
                </a>
              </div>
            )}

            <div className="form-group">
              <label>Location Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.locationName}
                onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                placeholder="e.g., FodyFee Downtown"
              />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>Address</label>
              <input
                type="text"
                className="form-control"
                value={formData.address}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                  searchAddress(e.target.value);
                }}
                placeholder="Search address..."
              />
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="list-group mt-1" style={{ position: 'absolute', zIndex: 1000, width: '100%' }}>
                  {addressSuggestions.map((place, index) => (
                    <button
                      key={index}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => selectAddress(place)}
                    >
                      <i className="fas fa-map-marker-alt mr-2 text-orange"></i>
                      {place.place_name}
                    </button>
                  ))}
                </div>
              )}
              {!MAPBOX_ACCESS_TOKEN && (
                <small className="form-text text-muted">
                  Mapbox token not configured. Add REACT_APP_MAPBOX_TOKEN to your environment.
                </small>
              )}
            </div>

            <div className="row">
              <div className="col-md-4 form-group">
                <label>City</label>
                <input type="text" className="form-control" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div className="col-md-4 form-group">
                <label>State</label>
                <input type="text" className="form-control" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </div>
              <div className="col-md-4 form-group">
                <label>ZIP</label>
                <input type="text" className="form-control" value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Country</label>
              <input type="text" className="form-control" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="US" />
            </div>

            <div className="row">
              <div className="col-md-6 form-group">
                <label>Phone Number</label>
                <input type="text" className="form-control" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" />
              </div>
              <div className="col-md-6 form-group">
                <label>Website URL</label>
                <input type="text" className="form-control" value={formData.websiteUrl} onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })} placeholder="https://yourstore.com" />
              </div>
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input type="text" className="form-control" value={formData.logoUrl} onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })} placeholder="https://your-cdn.com/logo.png" />
              {formData.logoUrl && (
                <div className="mt-2">
                  <img src={formData.logoUrl} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 200, borderRadius: 8, border: '1px solid #ddd' }} />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" className="form-control" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} placeholder="contact@yourstore.com" />
            </div>

            <div className="form-group">
              <div className="custom-control custom-switch">
                <input type="checkbox" className="custom-control-input" id="publishNowSwitch" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
                <label className="custom-control-label" htmlFor="publishNowSwitch">Publish immediately after creation</label>
              </div>
            </div>

            <div className="form-group">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="termsCheck"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="termsCheck">
                  I agree to the{' '}
                  <a href="https://fodyfee.com/terms/" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>{' '}
                  of the service.
                </label>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button className="btn btn-primary btn-lg" onClick={handleDeploy} disabled={deploying || !termsAccepted}>
                {deploying ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Deploying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket mr-2"></i>
                    Deploy Profile
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderManageForm = () => (
    <div className="row">
      <div className="col-lg-8 mx-auto">
        <div className="card card-outline card-primary">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h3 className="card-title">
              <i className="fas fa-store mr-2"></i>
              Business Information
            </h3>
            <div className="d-flex align-items-center">
              <span className={`badge badge-${publicStore.published ? 'success' : 'warning'} mr-3`} style={{ fontSize: '0.9rem', padding: '8px 12px' }}>
                {publicStore.published ? 'PUBLISHED' : 'PREPARING'}
              </span>
              <button className={`btn btn-sm btn-${publicStore.published ? 'outline-warning' : 'success'}`} onClick={handleTogglePublish}>
                {publicStore.published ? 'Unpublish' : 'Publish Now'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>Location Name</label>
              <input type="text" className="form-control" value={formData.locationName} onChange={(e) => setFormData({ ...formData, locationName: e.target.value })} />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>Address</label>
              <input
                type="text"
                className="form-control"
                value={formData.address}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                  searchAddress(e.target.value);
                }}
                placeholder="Search address..."
              />
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="list-group mt-1" style={{ position: 'absolute', zIndex: 1000, width: '100%' }}>
                  {addressSuggestions.map((place, index) => (
                    <button key={index} type="button" className="list-group-item list-group-item-action" onClick={() => selectAddress(place)}>
                      <i className="fas fa-map-marker-alt mr-2 text-orange"></i>
                      {place.place_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-md-4 form-group">
                <label>City</label>
                <input type="text" className="form-control" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div className="col-md-4 form-group">
                <label>State</label>
                <input type="text" className="form-control" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </div>
              <div className="col-md-4 form-group">
                <label>ZIP</label>
                <input type="text" className="form-control" value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Country</label>
              <input type="text" className="form-control" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
            </div>

            <div className="row">
              <div className="col-md-6 form-group">
                <label>Phone Number</label>
                <input type="text" className="form-control" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="col-md-6 form-group">
                <label>Website URL</label>
                <input type="text" className="form-control" value={formData.websiteUrl} onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input type="text" className="form-control" value={formData.logoUrl} onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })} placeholder="https://your-cdn.com/logo.png" />
              {formData.logoUrl && (
                <div className="mt-2">
                  <img src={formData.logoUrl} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 200, borderRadius: 8, border: '1px solid #ddd' }} />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" className="form-control" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} />
            </div>

            <div className="d-flex justify-content-between">
              <button className="btn btn-outline-info" onClick={handleReDeploy} disabled={syncing}>
                {syncing ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Syncing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt mr-2"></i>
                    Re-deploy / Refresh Data
                  </>
                )}
              </button>
              <button className="btn btn-primary" onClick={handleUpdateInfo} disabled={deploying}>
                {deploying ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="card card-outline card-success mt-4">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-info-circle mr-2"></i>
              Public Store Details
            </h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p className="mb-1"><strong>Store ID:</strong> {publicStore.id}</p>
                <p className="mb-1">
                  <strong>Status:</strong>{' '}
                  <span className={`badge badge-${publicStore.published ? 'success' : 'warning'}`}>
                    {publicStore.status || (publicStore.published ? 'published' : 'preparing')}
                  </span>
                </p>
                <p className="mb-1">
                  <strong>Created:</strong>{' '}
                  {publicStore.createdAt?.toDate ? publicStore.createdAt.toDate().toLocaleDateString() : 'N/A'}
                </p>
                <p className="mb-1">
                  <strong>Last Menu Sync:</strong>{' '}
                  {publicStore.menuSyncedAt?.toDate ? publicStore.menuSyncedAt.toDate().toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="col-md-6">
                {/* The public store link (what customers open) — was missing;
                    the merchant had no way to find/copy their store URL. */}
                <p className="mb-1">
                  <strong>Store URL:</strong>{' '}
                  <a href={`${process.env.REACT_APP_PUBLIC_WEB_URL || 'https://go.fodyfee.com'}/store/${(publicStore.locationName || 'store').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${publicStore.id}`} target="_blank" rel="noopener noreferrer" className="text-primary" style={{ wordBreak: 'break-all' }}>
                    {`${process.env.REACT_APP_PUBLIC_WEB_URL || 'https://go.fodyfee.com'}/store/${(publicStore.locationName || 'store').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${publicStore.id}`}
                  </a>
                  <button
                    className="btn btn-xs btn-outline-secondary ml-2"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(`${process.env.REACT_APP_PUBLIC_WEB_URL || 'https://go.fodyfee.com'}/store/${(publicStore.locationName || 'store').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${publicStore.id}`);
                      e.target.textContent = 'Copied!';
                      setTimeout(() => { e.target.textContent = 'Copy'; }, 1500);
                    }}
                  >Copy</button>
                </p>
                <p className="mb-1"><strong>Location:</strong> {publicStore.locationName}</p>
                <p className="mb-1"><strong>Phone:</strong> {publicStore.phone || 'N/A'}</p>
                <p className="mb-1">
                  <strong>Website:</strong>{' '}
                  {publicStore.websiteUrl ? (
                    <a href={publicStore.websiteUrl} target="_blank" rel="noopener noreferrer">{publicStore.websiteUrl}</a>
                  ) : 'N/A'}
                </p>
                <p className="mb-1">
                  <strong>Logo:</strong>{' '}
                  {publicStore.logoUrl ? (
                    <img src={publicStore.logoUrl} alt="Store logo" style={{ maxHeight: 40, borderRadius: 4, border: '1px solid #eee' }} />
                  ) : 'N/A'}
                </p>
                <p className="mb-1">
                  <strong>Coordinates:</strong>{' '}
                  {publicStore.latitude && publicStore.longitude
                    ? `${publicStore.latitude.toFixed(6)}, ${publicStore.longitude.toFixed(6)}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold mb-1">
            <i className="fas fa-globe text-primary mr-2"></i>
            Online Ordering
          </h4>
          <p className="text-muted mb-0">
            {publicStore
              ? `Public Store: ${publicStore.locationName || location.companyName || location.locationName || 'Unnamed'}`
              : 'Create a public profile so customers can order online.'}
          </p>
        </div>
      </div>
      {!publicStore ? renderDeployForm() : renderManageForm()}
    </div>
  );
};

export default SiteInformationTab;
